import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, { useContext, useMemo } from 'react';
import { useWatch } from 'antd/es/form/Form';

import {
    ModalTreePickerElement,
    SpecialJournalChildrenPickerElement,
    SpecialJournalChildrenPickerElementProps,
} from 'components/molecules';
import { AppFormContext } from 'contexts/AppFormContext';
import { formFields } from 'consts/form/formFields';
import { UserCheckBottomIcon } from 'assets';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useDisplayFetchDataError } from 'hooks/useDisplayFetchDataError/useDisplayFetchDataError';
import { groupsTexts, journalTexts } from 'consts/text';
import {
    mapGroupsListToChildrenIdsList,
    mapGroupsListToSelectOptions,
    parseGroupDataToJournalChildrenTreeNodes,
    parseGroupDataToTreeNodes,
} from 'utils/treePickerUtils';
import { GroupChildDTO, Child, ChildId, GroupDTO, GroupId, PaginationResponse } from 'types';
import { useToggle } from '../../../../hooks/useToggle/useToggle';

type SpecialJournalChildrenPickerProps = Pick<
    SpecialJournalChildrenPickerElementProps,
    'optional'
> & {
    addedChildren?: GroupChildDTO[];
};

export const SpecialJournalChildrenPicker = ({
    optional,
    addedChildren,
}: SpecialJournalChildrenPickerProps) => {
    const { setFieldValue } = useFormInstance();
    const { formError, setFormError } = useContext(AppFormContext);

    const [isModalOpen, openModal, closeModal] = useToggle(false);

    const childrenSelectValue = useWatch<string[] | undefined>(formFields.childIdsToAdd) || [];

    const {
        data: groupsData,
        isFetching: isGroupsDataFetching,
        isError: isGroupsDataError,
    } = useAppQuery('JOURNALS_GROUPS', [], StaffHTTPService.journals.getJournalsGroups, {});

    const { data: allChildrenResponse, isFetching: isAllChildrenFetching } = useAppQuery(
        'CHILDREN',
        [],
        () =>
            StaffHTTPService.children.getChildren({
                paginationParams: { size: '100000', page: '0' },
            }),
        {},
    );

    useDisplayFetchDataError({
        isFetching: isGroupsDataFetching,
        isError: isGroupsDataError,
        actualError: formError,
        errorMessage: groupsTexts.fetchingError,
        setError: setFormError,
    });

    const { mappedGroupsDataForPicker, mappedGroupsDataForTree, validChildrenIds } = useMemo(() => {
        if (!groupsData) {
            return {
                mappedGroupsDataForPicker: [],
                mappedGroupsDataForTree: [],
                validChildrenIds: [],
            };
        }

        const normalizedChildrenResponse = allChildrenResponse as
            | PaginationResponse<Child>
            | Child[]
            | undefined;
        const allChildren = Array.isArray(normalizedChildrenResponse)
            ? normalizedChildrenResponse
            : normalizedChildrenResponse?.content || [];

        let childrenWithoutGroup: GroupChildDTO[] = [];

        if (allChildren.length > 0) {
            childrenWithoutGroup = allChildren
                .filter((child) => !child.groups || child.groups.length === 0)
                .map((child) => ({
                    id: child.id,
                    firstName: child.firstName,
                    lastName: child.lastName,
                    joinDate: '',
                    leaveDate: null,
                }));

            if (addedChildren && addedChildren.length > 0) {
                const addedIds = addedChildren.map((c) => c.id);
                childrenWithoutGroup = childrenWithoutGroup.filter((c) => !addedIds.includes(c.id));
            }
        }

        const mappedDataForTree = addedChildren
            ? parseGroupDataToJournalChildrenTreeNodes(groupsData, addedChildren)
            : parseGroupDataToTreeNodes(groupsData);

        if (childrenWithoutGroup.length > 0) {
            const noGroupChildrenNodes = childrenWithoutGroup.map((child) => ({
                key: child.id,
                title: `${child.firstName} ${child.lastName}`,
            }));

            mappedDataForTree.push({
                key: 'no-group',
                title: 'Bez grupy',
                children: noGroupChildrenNodes,
            });
        }

        const fakeGroup: GroupDTO = {
            id: 'no-group' as GroupId,
            institutionId: '',
            name: 'Bez grupy',
            teachers: [],
            formerTeachers: [],
            childrenArray: childrenWithoutGroup,
            formerChildren: [],
            status: 'ACTIVE',
            type: 'JOURNAL_GROUP',
            schoolYears: [],
        };

        const combinedGroups =
            childrenWithoutGroup.length > 0 ? [...groupsData, fakeGroup] : groupsData;

        return {
            mappedGroupsDataForPicker: mapGroupsListToSelectOptions(combinedGroups),
            mappedGroupsDataForTree: mappedDataForTree,
            validChildrenIds: mapGroupsListToChildrenIdsList(combinedGroups),
        };
    }, [groupsData, allChildrenResponse, addedChildren]);

    const onClear = () => setFieldValue(formFields.childIdsToAdd, null);

    const onSave = (values: React.Key[]) => {
        const filteredSelectedValues = values.filter((value) =>
            validChildrenIds.includes(value as ChildId),
        ) as ChildId[];

        setFieldValue(formFields.childIdsToAdd, filteredSelectedValues);
        closeModal();
    };

    return (
        <>
            <ModalTreePickerElement
                title={journalTexts.journals.addChildren}
                open={isModalOpen}
                onCancel={closeModal}
                onSave={onSave}
                treeData={mappedGroupsDataForTree}
                initialValue={childrenSelectValue}
                icon={<UserCheckBottomIcon />}
                isLoading={isGroupsDataFetching || isAllChildrenFetching}
            />
            <SpecialJournalChildrenPickerElement
                onClear={onClear}
                onClick={openModal}
                isLoading={isGroupsDataFetching || isAllChildrenFetching}
                optional={optional}
                hasOptionalText={optional}
                options={mappedGroupsDataForPicker}
            />
        </>
    );
};
